import { Sentry } from './sentry';
import { redactForLogging } from './redact';
import { isLoggingOut } from '@shared/services/firebase';

/**
 * Centralized logging.
 *
 * Why this exists: the production build strips direct `console.x(...)` *calls*
 * via esbuild `drop` (see vite.config.ts), but it does NOT strip reference-style
 * usage like `.catch(console.error)` — those survive into prod, log to the
 * console, and never reach Sentry. Route errors through `logError` instead.
 *
 * Environment split:
 * - dev  → console only (nothing sent to Sentry; keeps dev logs useful + Sentry clean)
 * - prod → Sentry only  (console.x calls are stripped from the prod bundle)
 *
 * - `logError` reports to Sentry in prod, logs to the console in dev.
 * - `logWarn` / `logDebug` are dev-only console output (no-ops in prod).
 *
 * Never log PII (emails, tokens, names, addresses) — pass an error object and
 * optional non-PII `context` instead. As a backstop, `context` is run through
 * `redactForLogging` (lib/redact.ts) before it reaches Sentry, which scrubs
 * financial fields (balances, amounts, account numbers), tokens, and contact
 * details by key and by value pattern. Dev console output stays unredacted —
 * it never leaves the machine and raw values help debugging.
 *
 * permission-denied is deliberately NOT blanket-filtered: outside logout it's the
 * signal that catches a Firestore rule we forgot to deploy. We only drop it during
 * the brief logout window, when every live listener fires it as expected noise.
 */

const isDev = import.meta.env.DEV;

/**
 * Console logging is on for the Vite dev server AND for builds explicitly
 * marked as the dev or test app (the dev native binaries, dev web, CI).
 *
 * Deliberately an ALLOWLIST, not `!== 'production'`: if VITE_APP_ENV is
 * missing or unrecognised the safe answer is silence, not verbose output in
 * something that might be a real build.
 *
 * `import.meta.env.DEV` alone is false in EVERY built app — including the dev
 * Android/iOS binaries — so `logWarn`/`logDebug` compiled to no-ops exactly
 * where on-device debugging happens. Chasing the Android paywall on
 * 2026-09-10 meant reading RevenueCat's native logs because none of our own
 * calls existed in the binary. Pairs with vite.config's `esbuild.drop` (keeps
 * the console statements) and capacitor.config's `loggingBehavior: 'debug'`
 * (forwards them to logcat).
 *
 * Production is unchanged: no console output, errors to Sentry only.
 */
const consoleLoggingEnabled =
  isDev ||
  import.meta.env.VITE_APP_ENV === 'development' ||
  import.meta.env.VITE_APP_ENV === 'test';

/** permission-denied errors are expected (not actionable) while auth is being revoked on logout. */
function isExpectedLogoutNoise(error: unknown): boolean {
  if (!isLoggingOut()) return false;
  const code = (error as { code?: string } | null)?.code ?? '';
  const message = error instanceof Error ? error.message : String(error);
  return (
    code === 'permission-denied' ||
    message.includes('permission-denied') ||
    message.includes('Missing or insufficient permissions')
  );
}

/**
 * Report an error: Sentry in prod, console in dev. Safe to use as a `.catch` handler.
 *
 * Two `context` keys get special treatment for Sentry searchability:
 * - `context` (string operation slug, the house convention) is also emitted as
 *   a Sentry tag, so events are filterable by operation.
 * - `tags` is reserved: it becomes additional Sentry tags instead of extra
 *   data, e.g. `logError(err, { tags: { auth_method: 'google' } })`.
 */
export function logError(error: unknown, context?: Record<string, unknown>): void {
  // Console wherever console logging is on — the Vite dev server AND dev/test
  // binaries. Additive: the Sentry branch below is unchanged, so a dev binary
  // now reports to BOTH (visible on the device, still captured in dev Sentry)
  // and production still reports to Sentry alone.
  if (consoleLoggingEnabled) {
    // eslint-disable-next-line no-console
    console.error(error, context ?? '');
  }
  if (!isDev && !isExpectedLogoutNoise(error)) {
    if (!context) {
      Sentry.captureException(error);
      return;
    }
    const { tags, ...extra } = context;
    const mergedTags: Record<string, unknown> = {
      ...(typeof extra.context === 'string' ? { context: extra.context } : undefined),
      ...(tags && typeof tags === 'object' ? (tags as Record<string, unknown>) : undefined),
    };
    Sentry.captureException(error, {
      ...(Object.keys(mergedTags).length > 0
        ? { tags: redactForLogging(mergedTags) as Record<string, string> }
        : undefined),
      ...(Object.keys(extra).length > 0
        ? { extra: redactForLogging(extra) as Record<string, unknown> }
        : undefined),
    });
  }
}

/** Dev-only warning. No-op in production. */
export function logWarn(message: string, ...args: unknown[]): void {
  if (consoleLoggingEnabled) {
    // eslint-disable-next-line no-console
    console.warn(message, ...args);
  }
}

/** Dev-only debug log. No-op in production. */
export function logDebug(message: string, ...args: unknown[]): void {
  if (consoleLoggingEnabled) {
    // eslint-disable-next-line no-console
    console.log(message, ...args);
  }
}
